import { API_URL } from "../constants";
import type { Conversation, ConversationReview, ReviewEntry } from "@common/domain/conversation-results";

export interface ResultSet {
  setId: string;
  name: string;
  fileName: string;
  conversationCount: number;
  scenarioCount: number;
  runCount: number;
  uploadedBy: string;
  uploadedByEmail?: string;
  createdAt?: string;
}

export interface ResultSetContents {
  set: ResultSet;
  conversations: Conversation[];
  reviews: ConversationReview[];
  /** The signed-in user id as the API sees it, to find their own review. */
  viewerId: string;
  /** Reviewers per conversation, counting everyone. */
  reviewCounts: Record<string, number>;
  /** "own": reviews holds only the caller's (blind reviews); "all": every review (super admins). */
  reviewScope: "all" | "own";
}

async function request<T>(token: string | undefined, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...init.headers },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
  return body as T;
}

export const listResultSets = (token?: string) =>
  request<{ sets: ResultSet[] }>(token, "/conversation-results").then((b) => b.sets);

export const uploadResultSet = (token: string | undefined, fileName: string, content: string) =>
  request<{ set: ResultSet }>(token, "/conversation-results", { method: "POST", body: JSON.stringify({ fileName, content }) })
    .then((b) => b.set);

export const getResultSet = (token: string | undefined, setId: string) =>
  request<ResultSetContents>(token, `/conversation-results/${encodeURIComponent(setId)}`);

export const deleteResultSet = (token: string | undefined, setId: string) =>
  request(token, `/conversation-results/${encodeURIComponent(setId)}`, { method: "DELETE" });

const reviewPath = (setId: string, conversationId: string) =>
  `/conversation-results/${encodeURIComponent(setId)}/reviews/${encodeURIComponent(conversationId)}`;

export const saveReview = (
  token: string | undefined, setId: string, conversationId: string,
  review: { turns: Record<string, ReviewEntry>; overall: ReviewEntry },
) => request<{ review: ConversationReview }>(token, reviewPath(setId, conversationId), { method: "PUT", body: JSON.stringify(review) })
  .then((b) => b.review);

export const deleteReview = (token: string | undefined, setId: string, conversationId: string) =>
  request(token, reviewPath(setId, conversationId), { method: "DELETE" });
